import SideMenu from "../../features/sideMenu/SideMenu";
import { itemsProjects } from "../../constants/projects";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../ui/button/Button";
import {
  Box,
  Stack,
  Checkbox,
  IconButton,
  Typography,
  Chip,
  Badge,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ReactComponent as CompletadoIcon } from "../../assets/img/svg/notifications/completado.svg";
import { ReactComponent as PendienteIcon } from "../../assets/img/svg/notifications/pendiente.svg";
import { ReactComponent as ErrorIcon } from "../../assets/img/svg/notifications/error.svg";
import "./NotificationsPage.css";
import CheckboxWithMenu from "../../ui/checkBoxWithMenu/CheckBoxWithMenu";
import {
  getNotifications,
  deleteNotification,
  markAsRead,
  markAsUnread,
} from "../../components/functions/Notifications";
import { parseDateWithMonth } from "../../utils/notificationsHelpers";
import NotificationsModal from "./NotificationsModal";
import SendIcon from "@mui/icons-material/Send";

const STATUS = {
  error: {
    label: "Error",
    tone: {
      border: "#ffebee",
      bg: "rgba(242, 242, 242, 0.39)",
      text: "#a9151f",
      chipBg: "#ffcdd2",
      chipText: "#a9151f",
      icon: <ErrorIcon width={20} />,
      ring: "rgba(229, 57, 53, 0.35)",
      accent: "#e53935",
    },
  },
  pendiente: {
    label: "Pendiente",
    tone: {
      border: "#fff3e0",
      bg: "rgba(242, 242, 242, 0.39)",
      text: "#DE8509",
      chipBg: "#ffe0b2",
      chipText: "#a15c00",
      icon: <PendienteIcon width={20} />,
      ring: "rgba(251, 140, 0, 0.35)",
      accent: "#fb8c00",
    },
  },
  completado: {
    label: "Completado",
    tone: {
      border: "rgba(98, 119, 118, 0.57)",
      bg: "rgba(242, 242, 242, 0.39)",
      text: "#223D51",
      chipBg: "#c8e6c9",
      chipText: "#1b5e20",
      icon: <CompletadoIcon width={20} />,
      ring: "rgba(46, 125, 50, 0.35)",
      accent: "#2e7d32",
    },
  },
};

function NotificationCard({
  item,
  checked,
  onToggle,
  navigate,
  API_URL,
  setData,
  setFilteredData,
}) {
  const s = STATUS[item.estatus].tone;
  return (
    <Box
      sx={{
        border: `1px solid ${s.accent}`,
        backgroundColor: s.bg,
        width: "100%",
        display: "flex",
        overflow: "hidden",
        maxWidth: "100%",
        boxSizing: "border-box",
        px: 1.5,
        py: 1,
        mb: 1.5,
        minHeight: "max-content",
        borderRadius: 2,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={1.25}
        sx={{ minHeight: 60, minWidth: 0, flex: 1 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            width: { xs: "100%", md: "auto" },
            minHeight: 30,
            minWidth: 0,
            flex: { md: 1 },
          }}
        >
          <Checkbox
            checked={checked}
            onChange={() => onToggle(item.id)}
            sx={{ p: 0.5 }}
          />
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              color: s.accent,
              flex: "0 0 auto",
            }}
          >
            {STATUS[item.estatus].tone.icon}
          </Box>

          <p
            title={item.titulo}
            style={{
              flex: 1,
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: s.text,
              textAlign: "left",
              fontWeight: item.leido ? "normal" : "bold",
            }}
          >
            {item.titulo}
          </p>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{
            width: "auto",
            minHeight: 30,
            minWidth: 0,
            flex: { md: "0 1 auto" },
            flexWrap: "nowrap",
          }}
        >
          <div className="date-ago-container">
            <Typography variant="body2" sx={{ minWidth: 80, color: s.text }}>
              {item.date}
            </Typography>
            <Typography variant="body2" sx={{ minWidth: 80, color: s.text }}>
              {item.time}
            </Typography>
          </div>

          <CustomButton
            text={"Ver detalles"}
            color={"#223D51"}
            background={"#FFFFFF"}
            fontStyle={"normal"}
            fontWeight={500}
            fontSize={"14px"}
            width={"120px"}
            height={"auto"}
            type={"button"}
            border={"1px solid #223D51"}
            onClick={async () => {
              await markAsRead(API_URL, item.id);
              navigate(`${item.url}`);
            }}
          />
          <Chip
            label={STATUS[item.estatus].label}
            size="small"
            sx={{
              color: s.chipText,
              backgroundColor: s.chipBg,
              fontWeight: 500,
              borderRadius: "4px",
              height: 28,
              width: 100,
            }}
          />
          <IconButton
            onClick={() => {
              deleteNotification(API_URL, item.id);
              setData((prev) => prev.filter((n) => n.id !== item.id));
              setFilteredData((prev) => prev.filter((n) => n.id !== item.id));
            }}
            size="small"
            sx={{ ml: 0.5 }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );
}

const NotificationsPage = ({ API_URL, user, companies }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [status, setStatus] = useState("all");

  const [selected, setSelected] = useState(() => new Set());
  const allIds = useMemo(() => data.map((n) => n.id), [data]);

  const [allChecked, setAllChecked] = useState(false);
  const [someChecked, setSomeChecked] = useState(false);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    const allChecked = selected.size === allIds.length && allIds.length > 0;
    const someChecked = selected.size > 0 && !allChecked;
    setAllChecked(allChecked);
    setSomeChecked(someChecked);
  }, [selected]);

  const [counts, setCounts] = useState({
    error: 0,
    completado: 0,
    pendiente: 0,
    all: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const responseNotificaciones = await getNotifications(API_URL, user.id);

        responseNotificaciones.forEach((item) => {
          const { date, time } = parseDateWithMonth(item.created_at);
          item.date = date;
          item.time = time;
        });

        setData(responseNotificaciones);
        setFilteredData(responseNotificaciones);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const counts = {
      error: data.filter((n) => n.estatus === "error" && n.leido === 0).length,
      completado: data.filter(
        (n) => n.estatus === "completado" && n.leido === 0
      ).length,
      pendiente: data.filter((n) => n.estatus === "pendiente" && n.leido === 0)
        .length,
      all: data.filter((n) => n.leido === 0).length,
    };
    setCounts(counts);
  }, [data]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = (checked) => {
    setSelected(checked ? new Set(allIds) : new Set());
  };

  useEffect(() => {
    if (status === "all") {
      setFilteredData(data);
    } else {
      const filtered = data.filter((n) => n.estatus === status);
      setFilteredData(filtered);
    }
    setSelected(new Set());
  }, [status]);

  const handleMenuAction = (action) => {
    if (action === "delete") {
      const idsToDelete = Array.from(selected);
      // Luego crear un endpoint para eliminar las notificaciones en lote
      idsToDelete.forEach((id) => deleteNotification(API_URL, id));
      setData((prev) => prev.filter((n) => !selected.has(n.id)));
      setFilteredData((prev) => prev.filter((n) => !selected.has(n.id)));
      setSelected(new Set());
    } else if (action === "read" || action === "unread") {
      // Luego crear un endpoint para actualizar las notificaciones en lote
      const idsToUpdate = Array.from(selected);
      idsToUpdate.forEach((id) => {
        if (action === "read") {
          markAsRead(API_URL, id);
          const notif = data.find((n) => n.id === id);
          if (notif) notif.leido = 1;
        } else {
          const notif = data.find((n) => n.id === id);
          if (notif) notif.leido = 0;
          markAsUnread(API_URL, id);
        }
      });
      setSelected(new Set());
      const counts = {
        error: data.filter((n) => n.estatus === "error" && n.leido === 0)
          .length,
        completado: data.filter(
          (n) => n.estatus === "completado" && n.leido === 0
        ).length,
        pendiente: data.filter(
          (n) => n.estatus === "pendiente" && n.leido === 0
        ).length,
        all: data.filter((n) => n.leido === 0).length,
      };
      setCounts(counts);
    }
  };

  return (
    <div className="pp-container" style={{ display: "flex", width: "100%" }}>
      <NotificationsModal
        API_URL={API_URL}
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={() => {}}
        companies={companies}
      />
      <div className="pp-sidebar" style={{ width: "12rem" }}>
        <SideMenu items={itemsProjects} />
      </div>
      <div className="notifications-main">
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <h2 style={{ margin: 0 }}>Notificaciones ({filteredData.length})</h2>{" "}
          <IconButton onClick={() => setOpen(true)}>
            <SendIcon htmlColor="rgb(34, 61, 81)" />
          </IconButton>
        </div>
        <div
          className="notifications-button-container"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#fff",
            padding: "12px",
            borderRadius: "8px",
            gap: "12px",
          }}
        >
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <CheckboxWithMenu
              checked={allChecked}
              indeterminate={someChecked}
              onChange={(e) => toggleAll(e.target.checked)}
              options={[
                { label: "Borrar", value: "delete" },
                { label: "Marcar como leído", value: "read" },
                { label: "Marcar como no leído", value: "unread" },
              ]}
              onOptionSelect={(opt) => {
                handleMenuAction(opt.value);
              }}
            />
            <IconButton>
              <RefreshRoundedIcon />
            </IconButton>
          </div>
          <div className="notifications-button-filter-container">
            <Badge badgeContent={counts.error} color="error">
              <CustomButton
                className="notifications-button"
                text={"Error"}
                color={status === "error" ? "#223D51" : "#FFFFFF"}
                border={status === "error" ? "1px solid #223D51" : "none"}
                background={status === "error" ? "#FFFFFF" : "#223D51"}
                fontStyle={"normal"}
                fontWeight={500}
                fontSize={"14px"}
                height={"28px"}
                type={"button"}
                onClick={() => setStatus("error")}
              />
            </Badge>
            <Badge badgeContent={counts.completado} color="error">
              <CustomButton
                className="notifications-button"
                text={"Completado"}
                color={status === "completado" ? "#223D51" : "#FFFFFF"}
                border={status === "completado" ? "1px solid #223D51" : "none"}
                background={status === "completado" ? "#FFFFFF" : "#223D51"}
                fontStyle={"normal"}
                fontWeight={500}
                fontSize={"14px"}
                height={"28px"}
                type={"button"}
                onClick={() => setStatus("completado")}
              />
            </Badge>
            <Badge badgeContent={counts.pendiente} color="error">
              <CustomButton
                className="notifications-button"
                text={"Pendiente"}
                color={status === "pendiente" ? "#223D51" : "#FFFFFF"}
                border={status === "pendiente" ? "1px solid #223D51" : "none"}
                background={status === "pendiente" ? "#FFFFFF" : "#223D51"}
                fontStyle={"normal"}
                fontWeight={500}
                fontSize={"14px"}
                height={"28px"}
                type={"button"}
                onClick={() => setStatus("pendiente")}
              />
            </Badge>
            <Badge badgeContent={counts.all} color="error">
              <CustomButton
                className="notifications-button"
                text={"Todo"}
                color={status === "all" ? "#223D51" : "#FFFFFF"}
                background={status === "all" ? "#FFFFFF" : "#223D51"}
                border={status === "all" ? "1px solid #223D51" : "none"}
                fontStyle={"normal"}
                fontWeight={500}
                fontSize={"14px"}
                height={"28px"}
                type={"button"}
                onClick={() => setStatus("all")}
              />
            </Badge>
          </div>
        </div>
        <div className="notifications-container">
          {filteredData.map((n) => (
            <NotificationCard
              key={n.id}
              item={n}
              checked={selected.has(n.id)}
              onToggle={toggleOne}
              navigate={navigate}
              API_URL={API_URL}
              setData={setData}
              setFilteredData={setFilteredData}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
